import { useEffect } from 'react';

import { useRouter } from 'next/router';

import { getScrollPositionForSrp, setSrpPathInSessionStorage, srpPatterns } from '@/utilities/srpPathFromSessionStorageUtils';

/**
 * Smoothly scrolls the window to the specified position using requestAnimationFrame.
 * Ensures the DOM is ready before attempting to scroll.
 *
 * @param {Object} position - The scroll position coordinates
 * @param {number} position.left - Horizontal scroll position
 * @param {number} position.top - Vertical scroll position
 */
const scroll = ({ left, top }) => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
        window.scrollTo({
            left,
            top,
            behavior: 'smooth',
        });
    });
};

/**
 * Custom hook that manages SRP navigation state and scroll position restoration.
 *
 * This hook automatically:
 * - Detects when the user is on an SRP page using URL pattern matching
 * - Captures the SRP path and current scroll position when navigating away
 * - Restores the scroll position when returning to an SRP page
 *
 * The hook uses Next.js router events to track navigation and session storage
 * to persist data across page transitions. It only activates on pages that
 * match known SRP URL patterns.
 *
 * @hook
 * @returns {void} This hook doesn't return any values - it manages side effects only
 */
const useLastSrpUrl = () => {
    const { asPath, basePath, events } = useRouter();
    const completePath = [basePath, asPath].join('');
    const isSrpUrl = srpPatterns.some((pattern) => pattern.test(completePath));

    /**
     * Effect that captures SRP data when navigating away from SRP pages.
     * Listens to 'routeChangeStart' to store the current SRP path and scroll position
     * before the user leaves the page.
     */
    useEffect(() => {
        const handleHistoryChange = () => {
            if (!isSrpUrl) return;
            setSrpPathInSessionStorage({ path: asPath });
        };

        events.on('beforeHistoryChange', handleHistoryChange);
        return () => events.off('beforeHistoryChange', handleHistoryChange);
    }, [events, asPath, isSrpUrl]);

    /**
     * Effect that restores scroll position when arriving at SRP pages.
     * Listens to 'routeChangeComplete' to restore the previously saved scroll position
     * after the page has finished loading.
     */
    useEffect(() => {
        const handleSrpScrollRestoration = () => {
            if (!isSrpUrl) return;

            const { left, top } = getScrollPositionForSrp();

            if (!left && !top) return;

            scroll({ left, top });
        };

        events.on('routeChangeComplete', handleSrpScrollRestoration);
        return () => events.off('routeChangeComplete', handleSrpScrollRestoration);
    }, [events, isSrpUrl]);
};

export default useLastSrpUrl;
